// Landing Page - Premium, honest, product-focused
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Route, Camera, FileText, Truck, Calculator,
  Globe, ArrowRight, Check, Shield, MapPin, Receipt, 
  Navigation, Zap, Star, ChevronRight, Play,
  Sun, Moon, Smartphone, BarChart3, Clock, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';

const Landing = () => {
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);

  const features = [
    {
      icon: Navigation,
      title: 'GPS Professionnel',
      description: 'Suivi temps réel avec calcul précis des kilomètres. Itinéraires optimisés et multi-arrêts.',
      color: 'bg-blue-500'
    },
    {
      icon: Camera,
      title: 'Scan IA de Reçus',
      description: 'Photographiez vos reçus, l\'IA extrait les données automatiquement. Gain de temps garanti.',
      color: 'bg-purple-500'
    },
    {
      icon: FileText,
      title: 'Facturation Pro',
      description: 'Créez et envoyez vos factures par email en quelques clics. Export PDF professionnel.',
      color: 'bg-emerald-500'
    },
    {
      icon: Calculator,
      title: 'Comptabilité Simplifiée',
      description: 'Calcul automatique des IK, bénéfice net, exports pour votre comptable.',
      color: 'bg-amber-500'
    },
    {
      icon: Truck,
      title: 'Gestion de Flotte',
      description: 'Gérez plusieurs véhicules et conducteurs. Alertes maintenance incluses.',
      color: 'bg-rose-500'
    },
    {
      icon: Globe,
      title: 'Multi-langues',
      description: '6 langues (FR, EN, ES, IT, PT, AR) et 4 devises pour une utilisation internationale.',
      color: 'bg-cyan-500'
    }
  ];

  const plans = [
    {
      name: 'Gratuit',
      price: '0',
      period: '€',
      description: 'Pour découvrir OptiTrajet',
      features: ['3 trajets/mois', 'Suivi basique', 'Export manuel'],
      cta: 'Commencer gratuitement',
      popular: false
    },
    {
      name: 'Pro',
      price: '4.99',
      period: '€/mois',
      description: 'Pour les indépendants',
      features: ['Trajets illimités', 'GPS temps réel', 'Scan IA reçus', 'Facturation', 'Tous exports'],
      cta: 'Essai 7 jours gratuit',
      popular: true
    },
    {
      name: 'Pro+',
      price: '14.99',
      period: '€/mois',
      description: 'Pour les professionnels',
      features: ['Tout Pro +', 'Comptabilité avancée', 'Espace comptable', 'IK automatique', 'Support prioritaire'],
      cta: 'Essai 7 jours gratuit',
      popular: false
    },
    {
      name: 'Fleet',
      price: '99',
      period: '€/mois',
      description: 'Pour les entreprises',
      features: ['Tout Pro+ +', 'Multi-véhicules', 'Multi-conducteurs', 'API dédiée', 'Formation incluse'],
      cta: 'Nous contacter',
      popular: false
    }
  ];

  const testimonials = [
    {
      name: 'Marc D.',
      role: 'Chauffeur VTC, Paris',
      content: 'OptiTrajet m\'a fait gagner 2h par semaine sur ma comptabilité. Le scan des reçus est magique.',
      rating: 5
    },
    {
      name: 'Sophie L.',
      role: 'Taxi indépendante, Lyon',
      content: 'Enfin une application qui comprend les besoins des professionnels de la route. Interface claire et intuitive.',
      rating: 5
    },
    {
      name: 'Thomas R.',
      role: 'Gestionnaire de flotte, Marseille',
      content: 'Le suivi multi-véhicules est parfait pour notre entreprise. ROI en moins d\'un mois.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-lg shadow-primary/25">
                <Route className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-foreground">OptiTrajet</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Fonctionnalités</a>
              <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Tarifs</a>
              <a href="#testimonials" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Témoignages</a>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')} 
                className="text-muted-foreground hover:text-foreground"
                data-testid="login-btn"
              >
                Connexion
              </Button>
              <Button 
                onClick={() => navigate('/register')} 
                className="bg-primary hover:bg-primary/90 shadow-button-primary"
                data-testid="register-btn"
              >
                S'inscrire
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-6 bg-primary/10 text-primary border-primary/20 hover:bg-primary/15">
                <Shield className="w-3 h-3 mr-1.5" />
                Conforme facturation 2026
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-tight">
                Gérez votre activité
                <span className="block text-primary mt-2">en toute simplicité</span>
              </h1>
              
              <p className="mt-6 text-lg text-muted-foreground max-w-lg">
                L'application tout-en-un pour VTC, Taxis et Livreurs. 
                Trajets, factures, dépenses et comptabilité réunis en un seul outil professionnel.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/register')}
                  className="h-12 px-8 bg-primary hover:bg-primary/90 shadow-button-primary text-base font-medium"
                  data-testid="hero-cta-btn"
                >
                  Commencer gratuitement
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="h-12 px-8 border-border text-foreground hover:bg-muted/50 text-base"
                >
                  J'ai déjà un compte
                </Button>
              </div>

              <p className="mt-4 text-sm text-muted-foreground flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-500" />
                Gratuit pour commencer • Sans carte bancaire
              </p>
            </motion.div>

            {/* Right - Product Preview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              {/* Dashboard Preview Card */}
              <div className="bg-white rounded-2xl shadow-2xl shadow-slate-200/50 border border-border/50 overflow-hidden">
                {/* Mini Header */}
                <div className="bg-[hsl(var(--sidebar-bg))] px-4 py-3 border-b border-border/50 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-amber-400" />
                    <div className="w-3 h-3 rounded-full bg-emerald-400" />
                  </div>
                  <span className="text-xs text-muted-foreground ml-2">OptiTrajet Dashboard</span>
                </div>
                
                {/* Dashboard Content Preview */}
                <div className="p-4">
                  {/* KPI Preview */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-emerald-50 rounded-xl p-3">
                      <p className="text-xs text-emerald-600 font-medium">Revenus</p>
                      <p className="text-xl font-semibold text-emerald-700 tabular-nums">2,450 €</p>
                      <p className="text-xs text-emerald-500">+12% ce mois</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-3">
                      <p className="text-xs text-blue-600 font-medium">Distance</p>
                      <p className="text-xl font-semibold text-blue-700 tabular-nums">1,234 km</p>
                      <p className="text-xs text-blue-500">87 trajets</p>
                    </div>
                  </div>
                  
                  {/* Map Preview */}
                  <div className="bg-slate-100 rounded-xl h-32 flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-purple-100" />
                    <div className="relative flex items-center gap-2 text-muted-foreground">
                      <Navigation className="w-5 h-5 text-primary" />
                      <span className="text-sm font-medium">GPS en temps réel</span>
                    </div>
                    {/* Fake route line */}
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 100">
                      <path 
                        d="M20,80 Q60,20 100,50 T180,30" 
                        fill="none" 
                        stroke="#6B4CFF" 
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray="5,5"
                      />
                      <circle cx="20" cy="80" r="6" fill="#10B981" />
                      <circle cx="180" cy="30" r="6" fill="#EF4444" />
                    </svg>
                  </div>
                  
                  {/* Recent trip preview */}
                  <div className="mt-4 p-3 bg-muted/30 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Paris → Orly</p>
                        <p className="text-xs text-muted-foreground">Aujourd'hui • 32 km</p>
                      </div>
                      <span className="text-sm font-semibold text-emerald-600">+45 €</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute -left-4 top-1/4 bg-white rounded-xl shadow-lg p-3 border border-border/50"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium">Facture envoyée</p>
                    <p className="text-xs text-muted-foreground">Il y a 2 min</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="absolute -right-4 bottom-1/4 bg-white rounded-xl shadow-lg p-3 border border-border/50"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Camera className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium">Reçu scanné</p>
                    <p className="text-xs text-muted-foreground">Carburant 45€</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 border-y border-border/50 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-semibold text-foreground tabular-nums">6</div>
              <p className="text-sm text-muted-foreground mt-1">Langues disponibles</p>
            </div>
            <div>
              <div className="text-3xl font-semibold text-foreground tabular-nums">4</div>
              <p className="text-sm text-muted-foreground mt-1">Devises supportées</p>
            </div>
            <div>
              <div className="text-3xl font-semibold text-foreground">100%</div>
              <p className="text-sm text-muted-foreground mt-1">Mobile-friendly</p>
            </div>
            <div>
              <div className="text-3xl font-semibold text-foreground">RGPD</div>
              <p className="text-sm text-muted-foreground mt-1">Données sécurisées</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Fonctionnalités</Badge>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
              Tout ce qu'il vous faut pour gérer votre activité
            </h2>
            <p className="mt-4 text-muted-foreground">
              Une application complète conçue spécialement pour les professionnels de la route
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full shadow-card border-border/50 hover:shadow-card-hover transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-4`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 sm:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Tarifs</Badge>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
              Des tarifs simples et transparents
            </h2>
            <p className="mt-4 text-muted-foreground">
              Commencez gratuitement, évoluez selon vos besoins
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className={`h-full relative shadow-card border-border/50 ${plan.popular ? 'ring-2 ring-primary shadow-lg' : ''}`}>
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-white border-0 shadow-lg">Populaire</Badge>
                    </div>
                  )}
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                    
                    <div className="mt-4 flex items-baseline">
                      <span className="text-4xl font-semibold text-foreground tabular-nums">{plan.price}</span>
                      <span className="text-muted-foreground ml-1">{plan.period}</span>
                    </div>
                    
                    <ul className="mt-6 space-y-3">
                      {plan.features.map((feature, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Check className="w-4 h-4 text-primary flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      className={`w-full mt-6 ${plan.popular ? 'bg-primary hover:bg-primary/90 shadow-button-primary' : 'bg-muted hover:bg-muted/80 text-foreground'}`}
                      onClick={() => navigate('/register')}
                      data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Témoignages</Badge>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
              Ils font confiance à OptiTrajet
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full shadow-card border-border/50">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, j) => (
                        <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-muted-foreground mb-4">"{testimonial.content}"</p>
                    <div>
                      <p className="font-medium text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28 bg-primary">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white">
              Prêt à simplifier votre gestion ?
            </h2>
            <p className="mt-4 text-white/80 max-w-lg mx-auto">
              Rejoignez les professionnels qui ont choisi OptiTrajet pour gérer leur activité au quotidien
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/register')}
              className="mt-8 h-12 px-8 bg-white text-primary hover:bg-white/90 text-base font-medium"
              data-testid="cta-final-btn"
            >
              Créer mon compte gratuit
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <p className="mt-4 text-sm text-white/60">
              Inscription en 30 secondes • Sans engagement
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border/50 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Route className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-foreground">OptiTrajet</span>
              <Badge variant="outline" className="text-xs">Marque déposée</Badge>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">Confidentialité</a>
              <a href="#" className="hover:text-foreground transition-colors">CGU</a>
              <a href="#" className="hover:text-foreground transition-colors">Contact</a>
            </div>
            
            <p className="text-sm text-muted-foreground">© 2026 OptiTrajet. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
